import { paymentScheduleAPI } from 'services/swagger-api';
import type {
  GenerateHbsRemiseResponse,
  GetHbsRemisesResponse,
  HbsConfirmUploadResponse,
  HbsRemiseListItem
} from 'api/payment-schedule';

/**
 * Appels de la zone admin « Remises CSV HBS » (prélèvements animaux PR_ANI).
 *
 * Une remise est identifiée par son id (Guid) — jamais par sa date. Pattern ABP
 * RemoteStreamContent : le POST de génération renvoie du JSON (generationId, fileName,
 * contractsCount, generationDate) ; le fichier se télécharge via GET /remises/{id}/csv.
 * Client généré `paymentScheduleAPI` (source de vérité : docs/api, schéma
 * paymentSchedule.yaml) ; variante `*Raw` sur le GET csv pour lire le nom de fichier
 * dans Content-Disposition (exposé en CORS).
 *
 * Le job back génère aussi automatiquement le 5 du mois à 4h (remise tracée même à
 * 0 contrat) — le POST manuel reste pour les tests / rattrapages.
 */

export interface HbsFileResult {
  blob: Blob;
  filename: string;
}

export type {
  GenerateHbsRemiseResponse,
  GetHbsRemisesResponse,
  HbsConfirmUploadResponse,
  HbsRemiseListItem
};

const FALLBACK_FILENAME = 'prelevements-hbs.csv';

function parseContentDispositionFilename(header: string | null): string | undefined {
  if (!header) return undefined;
  // Priorité au format RFC 5987 : filename*=UTF-8''nom%20encode.csv
  const encoded = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, ''));
    } catch {
      /* ignore */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim();
}

/** Extrait le message d'erreur ABP (error.message) d'une ResponseError du client généré. */
async function extractApiErrorMessage(err: unknown): Promise<string | undefined> {
  const response = (err as { response?: Response })?.response;
  if (response && typeof response.clone === 'function') {
    try {
      const body = await response.clone().json();
      return body?.error?.message;
    } catch {
      /* ignore */
    }
  }
  return undefined;
}

/**
 * Liste les remises HBS générées (+ contexte d'environnement pour le bandeau garde-fou).
 * Appelé au montage de l'écran, puis re-fetché après génération / confirmation.
 * 403 si la permission BackOffice.PrelevementsHbs.Read manque.
 */
export async function getHbsRemises(): Promise<GetHbsRemisesResponse> {
  try {
    return await paymentScheduleAPI.getHbsRemises();
  } catch (err) {
    const status = (err as { response?: Response })?.response?.status;
    if (status === 403) {
      throw new Error("Accès non autorisé aux remises HBS.");
    }
    throw new Error(
      (await extractApiErrorMessage(err)) ||
        'Erreur lors du chargement des remises HBS.'
    );
  }
}

/**
 * Génère une remise (POST, renvoie du JSON — pas le fichier). Effet de bord : marque
 * les contrats inclus comme transmis ; une remise est toujours tracée, même à 0 contrat.
 * Si contractsCount > 0, enchaîner sur downloadHbsRemise(generationId).
 */
export async function generateHbsRemise(): Promise<GenerateHbsRemiseResponse> {
  try {
    return await paymentScheduleAPI.generateHbsRemise();
  } catch (err) {
    throw new Error(
      (await extractApiErrorMessage(err)) ||
        'Erreur lors de la génération de la remise HBS.'
    );
  }
}

/**
 * Télécharge le fichier CSV d'une remise par son id (GET, sans effet de bord,
 * re-téléchargeable à volonté).
 */
export async function downloadHbsRemise(id: string): Promise<HbsFileResult> {
  try {
    const res = await paymentScheduleAPI.downloadHbsRemiseRaw({ id });
    const blob = await res.value();
    const filename =
      parseContentDispositionFilename(res.raw.headers.get('Content-Disposition')) ??
      FALLBACK_FILENAME;
    return { blob, filename };
  } catch (err) {
    throw new Error(
      (await extractApiErrorMessage(err)) || 'Impossible de récupérer la remise HBS.'
    );
  }
}

/** Confirme l'upload réel de la remise sur le portail HBS (POST, par id). */
export async function confirmHbsUpload(id: string): Promise<HbsConfirmUploadResponse> {
  try {
    // La notification de succès (OperationResponse.notification) est affichée automatiquement
    // par l'auth-interceptor lors du .json().
    return await paymentScheduleAPI.confirmHbsUpload({ id });
  } catch (err) {
    throw new Error(
      (await extractApiErrorMessage(err)) ||
        "Échec de la confirmation d'upload HBS."
    );
  }
}

/** Déclenche le téléchargement d'un blob dans le navigateur. */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
